// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * SoundManager - Voice Announcement System
 * 
 * Automatically discovers and plays voice announcements based on game events.
 * Features:
 * - Auto-discovery: Checks if voice files exist before playing
 * - Voice selection: Uses player's chosen voice from localStorage
 * - Queue system: Prevents overlapping announcements
 * - Volume control: Respects master volume setting
 * - Fallback: Gracefully handles missing files
 * 
 * Usage:
 *   import SoundManager from './utils/system/SoundManager';
 *   SoundManager.play('warp_engaged');
 *   SoundManager.play('red_alert', { priority: 'high' });
 */

import STORAGE_KEYS from './STORAGE_KEYS';

class SoundManagerClass {
  constructor() {
    this.audioContext = null;
    this.currentAudio = null;
    this.queue = [];
    this.isPlaying = false;
    this.volume = 0.7; // Default 70% volume
    this.enabled = true;
    this.voiceCache = new Map(); // Cache for file existence checks
    
    // Initialize audio context on first user interaction
    this.initialized = false;
    
    // Event-to-file mapping with category subdirectories
    // Format: { eventKey: { file: 'filename', category: 'subdirectory' } }
    this.eventMap = {
      // Navigation & Warp
      'warp_engaged': { file: 'warp_engaged', category: 'navigation' },
      'warp_disengaged': { file: 'warp_disengaged', category: 'navigation' },
      'course_set': { file: 'course_set', category: 'navigation' },
      'approaching_destination': { file: 'approaching_destination', category: 'navigation' },
      'destination_reached': { file: 'destination_reached', category: 'navigation' },
      'entering_orbit': { file: 'entering_orbit', category: 'navigation' },
      'systems_initializing': { file: 'systems_initializing', category: 'navigation' },
      
      // Fuel
      'low_fuel_warning': { file: 'low_fuel', category: 'navigation' },
      'fuel_critical': { file: 'fuel_critical', category: 'navigation' },
      'fuel_depleted': { file: 'fuel_depleted', category: 'navigation' },
      'refueling_complete': { file: 'refueling_complete', category: 'navigation' },
      
      // Combat
      'red_alert': { file: 'red_alert', category: 'combat' },
      'hostile_ships_detected': { file: 'hostile_detected', category: 'combat' },
      'incoming_fire': { file: 'incoming_fire', category: 'combat' },
      'shields_critical': { file: 'shields_critical', category: 'combat' },
      'target_destroyed': { file: 'target_destroyed', category: 'combat' },
      'all_clear': { file: 'all_clear', category: 'combat' },
      
      // Systems & Damage
      'hull_breach': { file: 'hull_breach', category: 'systems' },
      'power_critical': { file: 'power_critical', category: 'systems' },
      'all_systems_nominal': { file: 'systems_nominal', category: 'systems' },
      'repairs_complete': { file: 'repairs_complete', category: 'systems' },
      
      // Docking
      'docking_sequence_initiated': { file: 'docking_initiated', category: 'docking' },
      'docking_complete': { file: 'docking_complete', category: 'docking' },
      'undocking_complete': { file: 'undocking_complete', category: 'docking' },
      
      // Scanning
      'scanning': { file: 'scanning', category: 'scanning' },
      'scan_complete': { file: 'scan_complete', category: 'scanning' },
      'anomaly_detected': { file: 'anomaly_detected', category: 'scanning' },
      
      // Warnings
      'collision_imminent': { file: 'collision_warning', category: 'warnings' },
      'asteroid_field': { file: 'asteroid_field', category: 'warnings' },
      'quantum_storm': { file: 'quantum_storm', category: 'warnings' },
    };
  }

  /**
   * Initialize audio context (must be called after user interaction)
   */
  initialize() {
    if (this.initialized) return;
    
    try {
      // Create audio context
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      this.audioContext = new AudioContext();
      this.initialized = true;
    } catch (error) {
      console.error('[SoundManager] Failed to initialize audio context:', error);
      this.enabled = false;
    }
  }

  /**
   * Get the current voice selection from localStorage
   */
  getSelectedVoice() {
    return localStorage.getItem(STORAGE_KEYS.SHIP_VOICE) || 'matthew-ship-computer';
  }

  /**
   * Build the full path to a voice file
   */
  getVoiceFilePath(eventKey) {
    const voiceId = this.getSelectedVoice();
    const mapping = this.eventMap[eventKey];
    
    if (!mapping) {
      console.warn(`[SoundManager] No mapping found for event: ${eventKey}`);
      return null;
    }
    
    // Build path: /sounds/voice/{voiceId}/{category}/{filename}.mp3
    return `/sounds/voice/${voiceId}/${mapping.category}/${mapping.file}.mp3`;
  }

  /**
   * Check if a voice file exists (with caching)
   */
  async checkFileExists(url) {
    // Check cache first
    if (this.voiceCache.has(url)) {
      return this.voiceCache.get(url);
    }

    try {
      const response = await fetch(url, { method: 'HEAD' });
      const exists = response.ok;
      
      // Cache the result
      this.voiceCache.set(url, exists);
      
      return exists;
    } catch (error) {
      this.voiceCache.set(url, false);
      return false;
    }
  }

  /**
   * Play a voice announcement
   * @param {string} eventKey - Event identifier (e.g., 'warp_engaged')
   * @param {object} options - Playback options
   * @param {string} options.priority - 'low', 'medium', 'high', 'critical'
   * @param {boolean} options.interrupt - If true, interrupt current playback
   */
  async play(eventKey, options = {}) {
    if (!this.enabled) return;

    // Initialize on first play attempt
    if (!this.initialized) {
      this.initialize();
    }

    const {
      priority = 'medium',
      interrupt = false
    } = options;

    // Get file path
    const filePath = this.getVoiceFilePath(eventKey);
    if (!filePath) return;

    // Create announcement object
    const announcement = {
      eventKey,
      filePath,
      priority,
      interrupt
    };

    // Handle interrupt
    if (interrupt && this.isPlaying) {
      this.stop();
      this.queue = []; // Clear queue
    }

    // Add to queue
    this.queue.push(announcement);

    // Start playing if not already playing
    if (!this.isPlaying) {
      this.playNext();
    }
  }

  /**
   * Play the next announcement in the queue
   */
  async playNext() {
    if (this.queue.length === 0) {
      this.isPlaying = false;
      return;
    }

    this.isPlaying = true;
    const announcement = this.queue.shift();

    try {
      
      // Create audio element
      const audio = new Audio(announcement.filePath);
      audio.volume = this.volume;
      this.currentAudio = audio;

      // Play audio
      await audio.play();

      // Wait for audio to finish
      audio.onended = () => {
        this.currentAudio = null;
        this.playNext(); // Play next in queue
      };

      // Handle errors (missing files silently skipped)
      audio.onerror = () => {
        this.currentAudio = null;
        this.playNext(); // Continue to next
      };

    } catch (error) {
      // Silently ignore missing files and autoplay policy errors
      this.currentAudio = null;
      this.playNext(); // Continue to next
    }
  }

  /**
   * Stop current playback
   */
  stop() {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
      this.currentAudio = null;
    }
    this.isPlaying = false;
  }

  /**
   * Clear the announcement queue
   */
  clearQueue() {
    this.queue = [];
  }

  /**
   * Set master volume (0.0 to 1.0)
   */
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume));
    if (this.currentAudio) {
      this.currentAudio.volume = this.volume;
    }
    localStorage.setItem(STORAGE_KEYS.SHIP_VOICE_VOLUME, this.volume.toString());
  }

  /**
   * Get current volume
   */
  getVolume() {
    return this.volume;
  }

  /**
   * Enable/disable voice announcements
   */
  setEnabled(enabled) {
    this.enabled = enabled;
    if (!enabled) {
      this.stop();
      this.clearQueue();
    }
    localStorage.setItem(STORAGE_KEYS.SHIP_VOICE_ENABLED, enabled.toString());
  }

  /**
   * Check if voice announcements are enabled
   */
  isEnabled() {
    return this.enabled;
  }

  /**
   * Load settings from localStorage
   */
  loadSettings() {
    const savedVolume = localStorage.getItem(STORAGE_KEYS.SHIP_VOICE_VOLUME);
    if (savedVolume) {
      this.volume = parseFloat(savedVolume);
    }

    const savedEnabled = localStorage.getItem(STORAGE_KEYS.SHIP_VOICE_ENABLED);
    if (savedEnabled !== null) {
      this.enabled = savedEnabled === 'true';
    }

  }

  /**
   * Get list of all available events
   */
  getAvailableEvents() {
    return Object.keys(this.eventMap);
  }

  /**
   * Clear the file existence cache (useful when voice changes)
   */
  clearCache() {
    this.voiceCache.clear();
  }
}

// Create singleton instance
const SoundManager = new SoundManagerClass();

// Load settings on initialization
SoundManager.loadSettings();

// Export singleton
export default SoundManager;
